import { useCallback, useReducer, useRef, useState } from 'react';
import axios from 'axios';

import { headers } from '../../../../actions/defaults';
import { API_URL } from '../../../../environment';
import {
  createInitialLiveCaptionsState,
  liveCaptionsReducer,
} from './liveCaptionsReducer';

const startLiveCaptions = appointmentId =>
  axios.post(
    `${API_URL}/v1/appointments/${appointmentId}/live_captions`,
    null,
    headers(),
  );

export const useLiveCaptions = ({ appointment, resolveSpeaker }) => {
  const [captionState, dispatch] = useReducer(
    liveCaptionsReducer,
    undefined,
    createInitialLiveCaptionsState,
  );
  const [isEnabled, setIsEnabled] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [languageCode, setLanguageCode] = useState();
  const isEnabledRef = useRef(false);
  const startedAppointmentIdRef = useRef();
  const available = Boolean(appointment?.live_captions_available);
  const appointmentId = appointment?.id;

  const enableCaptions = useCallback(async () => {
    if (!available) return false;

    try {
      if (startedAppointmentIdRef.current !== appointmentId) {
        setIsStarting(true);
        const response = await startLiveCaptions(appointmentId);
        setLanguageCode(response.data?.language_code);
        startedAppointmentIdRef.current = appointmentId;
      }
      isEnabledRef.current = true;
      setIsEnabled(true);
      return true;
    } catch {
      isEnabledRef.current = false;
      setIsEnabled(false);
      return false;
    } finally {
      setIsStarting(false);
    }
  }, [appointmentId, available]);

  const disableCaptions = useCallback(() => {
    isEnabledRef.current = false;
    setIsEnabled(false);
    dispatch({ type: 'captionsDisabled' });
  }, []);

  const onCaptionReceived = useCallback(
    event => {
      if (!isEnabledRef.current) return;

      const speaker = resolveSpeaker(event);
      if (!speaker) return;

      dispatch({
        type: 'captionReceived',
        payload: {
          streamId: event.streamId,
          caption: event.caption,
          isFinal: event.isFinal,
          speaker,
        },
      });
    },
    [resolveSpeaker],
  );

  const onCaptionStreamDestroyed = useCallback(streamId => {
    dispatch({ type: 'streamDestroyed', streamId });
  }, []);

  const clearPartialCaptions = useCallback(() => {
    dispatch({ type: 'captionsDisabled' });
  }, []);

  return {
    available,
    captionState,
    isEnabled,
    isStarting,
    languageCode,
    enableCaptions,
    disableCaptions,
    onCaptionReceived,
    onCaptionStreamDestroyed,
    clearPartialCaptions,
  };
};
